import React, { useContext, useEffect, useState } from "react";
import { Socket } from "socket.io-client";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import INotification from "../../../models/INotification";
import IDiscount from "../../../models/IDiscount";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwButton from "../../shared/OwButton/OwButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { useFormik } from "formik";
import INewDiscount from "../../../models/INewDiscount";
import PriceUpchargeTypeEnum from "../../../models/PriceUpchargeTypeEnum";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import OwDateInput from "../../shared/OwDateInput/OwDateInput";
import { Moment } from "moment-timezone";
import IProcessDiscountDTO from "../../../models/DTO/IProcessDiscountDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnProcessDiscountDTO from "../../../models/DTO/IOnProcessDiscountDTO";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import OwCheckbox from "../../shared/OwCheckbox/OwCheckbox";
import moment from "moment";

type Props = {
    socket: Socket;
    setActivityIndicator: (activityOn: boolean, isInitialLoad?: boolean) => void;
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
    discount?: IDiscount;
    closeFn: (refreshData?: boolean) => void;
};

export const DiscountSidebar = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const [active, setIsActive] = useState(false);
    const [isDisabled, setIsDisabled] = useState(!props.discount);
    const [startDate, setStartDate] = useState<Moment | null>(props.discount?.start_date ? moment(props.discount.start_date) : null);
    const [endDate, setEndDate] = useState<Moment | null>(props.discount?.end_date ? moment(props.discount.end_date) : null);

    const discountTypeOptions: { title: string; value: number }[] = [
        { title: "fixed", value: PriceUpchargeTypeEnum.FIXED },
        { title: "percent", value: PriceUpchargeTypeEnum.PERCENT },
    ];

    const closeSidebar = () => {
        props.closeFn();
    };

    const processDiscount = (values: INewDiscount) => {
        const payload: IProcessDiscountDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            discount: {
                ...props.discount,
                ...values,
            },
        };

        props.socket.emit(EndpointsEnum.DiscountsProcessDiscount, payload);
    };

    const onProcessDiscount = (data: IOnProcessDiscountDTO) => {
        const notification: INotification = {
            title: "successfully_processed_discount",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        props.notifyFn(null, notification);

        props.closeFn(true);
    };

    const formik = useFormik({
        initialValues: {
            name: props.discount?.name || "",
            discount: props.discount?.discount || 0,
            discount_type_id: props.discount?.discount_type_id || PriceUpchargeTypeEnum.PERCENT,
            code: props.discount?.code || "",
            has_unique_codes: props.discount ? props.discount.has_unique_codes : false,
            start_date: props.discount?.start_date,
            end_date: props.discount?.end_date,
            active: props.discount ? props.discount.active : true,
        },
        onSubmit: (values) => {
            const data = {
                ...values,
                supplier_id: supplierModel.id,
                user_id: userModel.id,
            };

            processDiscount(data);
        },
        validate: (values) => {
            const errors: any = {};

            if (!values.name.length) {
                errors.name = "Required";
            }

            if (!values.discount) {
                errors.discount = "Required";
            }

            if (!values.discount_type_id) {
                errors.discount_type_id = "Required";
            }

            setIsDisabled(Object.keys(errors).length > 0);

            return errors;
        },
    });

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.DiscountsOnProcessDiscount, fn: onProcessDiscount }];

    useEffect(() => {
        for (const socket of sockets) {
            props.socket.on(socket.endpoint, socket.fn);
        }

        return () => {
            for (const socket of sockets) {
                props.socket.removeListener(socket.endpoint, socket.fn);
            }
        };
    }, []);

    return (
        <div className={"DiscountSidebar p-2"}>
            <div>
                <div className={"text-end"}>
                    <OwButton centerIconClass={"fas fa-times"} fn={closeSidebar} className={"btn-danger"} />
                </div>
                <OwTitle title={"discount"} />

                <form onSubmit={formik.handleSubmit}>
                    <div className="mt-2">
                        <div>
                            <OwLabel title={"name"} showIsReqAsterik={true} />
                        </div>
                        <div>
                            <OwFnInput fn={formik.handleChange} name={"name"} value={formik.values.name} />
                        </div>
                    </div>

                    <div className="mt-2">
                        <div>
                            <OwCheckbox
                                name={"active"}
                                checked={formik.values.active}
                                truthyVal={true}
                                falsyVal={false}
                                fn={formik.handleChange}
                                label={"active"}
                            />
                        </div>
                    </div>

                    <div className="mt-2">
                        <div>
                            <OwLabel title={"discount"} showIsReqAsterik={true} />
                        </div>
                        <div>
                            <OwFnInput fn={formik.handleChange} name={"discount"} value={formik.values.discount} />
                        </div>
                    </div>

                    <div className="mt-2">
                        <div>
                            <OwLabel title={"discount_type"} showIsReqAsterik={true} />
                        </div>
                        <div>
                            <OwSelectOption
                                fn={formik.handleChange}
                                name={"discount_type_id"}
                                titleProp={"title"}
                                valueProp={"value"}
                                options={discountTypeOptions}
                                selectedOption={formik.values}
                                selectedOptionProp={"discount_type_id"}
                                translateTitle={true}
                            />
                        </div>
                    </div>

                    <div className="mt-2">
                        <div>
                            <OwLabel title={"start_date"} />
                        </div>
                        <div>
                            <OwDateInput
                                fn={(date: Moment) => {
                                    setStartDate(date);
                                    formik.setFieldValue("start_date", date.format("YYYY-MM-DD"));
                                }}
                                selectedDate={startDate}
                                dateFormat={"MM/dd/yyyy"}
                            />
                        </div>
                    </div>

                    <div className="mt-2">
                        <div>
                            <OwLabel title={"end_date"} />
                        </div>
                        <div>
                            <OwDateInput
                                fn={(date: Moment) => {
                                    setEndDate(date);
                                    formik.setFieldValue("end_date", date.format("YYYY-MM-DD"));
                                }}
                                selectedDate={endDate}
                                dateFormat={"MM/dd/yyyy"}
                            />
                        </div>
                    </div>

                    <div className="mt-2">
                        <OwButton title={"save"} type={"submit"} active={active} disableOnActive={true} disabled={isDisabled} />
                    </div>
                </form>
            </div>
        </div>
    );
};
